import { useState } from "react";
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { DialogTitleWithClose } from "./DialogTitleWithClose";
import { AttachmentPreviewInline } from "./AttachmentPreviewInline";
import { attachmentPreviewSrc } from "../api/attachments";
import type { AttachmentDto } from "../api/types";

export type AttachmentPreviewProps = {
  attachment: AttachmentDto;
  onCreateInvoice?: () => void;
};

const AttachmentPreview = ({
  attachment,
  onCreateInvoice,
}: AttachmentPreviewProps) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // `undefined` when there's no stored preview — the Avatar then renders its
  // "PDF"/"FILE" child instead of firing a request that can only 404.
  const previewUrl = attachmentPreviewSrc(attachment);
  const isPdf = attachment.contentType === "application/pdf";
  const fallbackText = isPdf ? "PDF" : "FILE";

  return (
    <>
      <Avatar
        src={previewUrl}
        variant="rounded"
        // The thumbnail is the only way into the viewer on narrow screens, so
        // it has to be reachable by keyboard and announce what it opens.
        role="button"
        tabIndex={0}
        aria-label={`Open ${attachment.filename}`}
        sx={{ width: 56, height: 56, mr: 2, cursor: "pointer" }}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
      >
        {fallbackText}
      </Avatar>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        // On a phone the viewer is the whole point of the tap — give it the
        // whole screen rather than a letterboxed 80vh card.
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              minHeight: isMobile ? undefined : "80vh",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <DialogTitleWithClose
          onClose={handleClose}
          actions={
            onCreateInvoice && (
              <Button
                variant="contained"
                size="small"
                startIcon={<RequestQuoteIcon />}
                onClick={() => {
                  setOpen(false);
                  onCreateInvoice();
                }}
              >
                Create invoice
              </Button>
            )
          }
        >
          {attachment.filename}
        </DialogTitleWithClose>
        <DialogContent
          dividers
          sx={{
            p: 0,
            flexGrow: 1,
            display: "flex",
            overflow: "hidden",
            bgcolor: "#000",
            minHeight: 0,
          }}
        >
          <AttachmentPreviewInline attachment={attachment} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttachmentPreview;
